/**
 * Copyright (C) 2022 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import React from 'react';
import { Box, IconButton, Menu, MenuItem, Typography } from '@mui/material';
import Button from '@mui/material/Button';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import FirstPageRoundedIcon from '@mui/icons-material/FirstPageRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import LastPageRoundedIcon from '@mui/icons-material/LastPageRounded';
import isNil from 'lodash/isNil';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { fetchTickets } from '../../store/tickets/actions';
import { TicketsState } from '../../store/tickets/reducer';
import StyledProps, { ThemeInterface } from '../common/theme/AppThemeInterface';
import appTheme from '../common/theme/appTheme.json';
import TicketsTablPaginationControlsPageJumpForm from './TicketsTablePaginationControlsPageJumpForm';
import { EMPTY_FIELD_STRING_PLACEHOLDER } from '../../utils/StringUtils';

export interface TicketsTablePaginationControlsProps {
  tickets: Readonly<TicketsState>;
  theme: ThemeInterface;
}
const TicketsTablePaginationControls = (props: TicketsTablePaginationControlsProps) => {
  const { tickets, theme } = props;
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (pageSize: number) => {
    setAnchorEl(null);
    dispatch(fetchTickets({ ...tickets.options, page: 0, limit: pageSize }));
  };

  const dispatch = useDispatch();

  // Calculations
  const ticketsPerPage = (tickets.options || {}).limit;
  const totalTickets = tickets.total || 1;
  const lastPage = ticketsPerPage ? Math.ceil(totalTickets / ticketsPerPage) : null;
  return (
    <Box
      display="flex"
      justifyContent="space-between"
      color={theme.primaryTextColor}
      paddingLeft="24px"
      paddingRight="24px"
    >
      <Box display="flex" justifyContent="center" alignItems="center">
        <Box>Page Size:</Box>
        <Box paddingLeft="8px">
          <Button
            sx={{
              width: '64px',
              borderRadius: '5px',
              backgroundColor: appTheme.secondaryBackgroundColor,
              color: '#ffffff',
            }}
            fullWidth
            onClick={handleClick}
            endIcon={<KeyboardArrowDownRoundedIcon color="inherit" />}
          >
            <Box paddingRight="8px">{ticketsPerPage}</Box>
          </Button>
        </Box>
        <StyledPageSizeMenu
          id="simple-menu"
          anchorEl={anchorEl}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          keepMounted
          open={Boolean(anchorEl)}
          onClose={() => {
            setAnchorEl(null);
          }}
        >
          <MenuItem sx={{ justifyContent: 'flex-end' }} onClick={() => handleClose(10)}>
            {(tickets.options || {}).limit === 10 ? <StyledSelectedPageSizeIcon fontSize="small" /> : <></>}
            10
          </MenuItem>
          <MenuItem sx={{ justifyContent: 'flex-end' }} onClick={() => handleClose(25)}>
            {(tickets.options || {}).limit === 25 ? <StyledSelectedPageSizeIcon fontSize="small" /> : <></>}
            25
          </MenuItem>
          <MenuItem sx={{ justifyContent: 'flex-end' }} onClick={() => handleClose(50)}>
            {(tickets.options || {}).limit === 50 ? <StyledSelectedPageSizeIcon fontSize="small" /> : <></>}
            50
          </MenuItem>
        </StyledPageSizeMenu>
      </Box>
      <Box display="flex" justifyContent="center" alignItems="center">
        <Box color={theme.secondaryTextColor}>
          <IconButton
            color="inherit"
            onClick={() => {
              if (tickets.options) {
                dispatch(fetchTickets({ ...tickets.options, page: 0 }));
              }
            }}
          >
            <FirstPageRoundedIcon />
          </IconButton>
        </Box>
        <Box color={theme.secondaryTextColor}>
          <IconButton
            color="inherit"
            onClick={() => {
              if (tickets.options && !isNil(lastPage) && tickets.options.page) {
                dispatch(fetchTickets({ ...tickets.options, page: tickets.options.page - 1 }));
              }
            }}
          >
            <ChevronLeftRoundedIcon />
          </IconButton>
        </Box>
        <Typography>Page </Typography>
        <Box width="64px" padding="8px">
          <TicketsTablPaginationControlsPageJumpForm tickets={tickets} />
        </Box>
        <Box paddingRight="8px">
          <Typography>of</Typography>
        </Box>
        <Typography>{lastPage || EMPTY_FIELD_STRING_PLACEHOLDER}</Typography>
        <Box color={theme.secondaryTextColor}>
          <IconButton
            color="inherit"
            onClick={() => {
              if (tickets.options && !isNil(lastPage) && tickets.options.page < lastPage - 1) {
                dispatch(fetchTickets({ ...tickets.options, page: tickets.options.page + 1 }));
              }
            }}
          >
            <ChevronRightRoundedIcon />
          </IconButton>
        </Box>
        <Box color={theme.secondaryTextColor}>
          <IconButton
            color="inherit"
            onClick={() => {
              if (!isNil(lastPage)) {
                dispatch(fetchTickets({ ...tickets.options, page: lastPage - 1 }));
              }
            }}
          >
            <LastPageRoundedIcon />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
};
export default TicketsTablePaginationControls;

const StyledSelectedPageSizeIcon = styled(CheckRoundedIcon)`
  margin-right: 12px;
  color: ${({ theme }: StyledProps) => theme.buttonSelectedColor};
`;
const StyledPageSizeMenu = styled(Menu)`
  div {
    background-color: ${({ theme }: StyledProps) => theme.primaryBackgroundColor};
  }
`;